import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { useTheme } from '@mui/material/styles'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import useModals from '@/v2/hooks/useModals'
import { useEligibilities } from '@/v2/hooks/insurance/useEligibilities'
import RTEligibilities from '@/v2/hooks/subscriptions/messages/RTEligibilities'

const EligibilityCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const ProviderName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`

const StatusBadge = styled.div`
  background: ${({ bgColor }) => bgColor || '#f5f5f5'};
  color: ${({ color }) => color || '#666'};
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 16px;
`

const InfoLabel = styled.div`
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
`

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  text-align: right;
  word-break: break-word;
`

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  font-size: 14px;
  background: #fff;
  border-radius: 12px;
`

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  font-size: 14px;
`

const EligibilitiesTabMobile = ({ onClaimClick }) => {
  const theme = useTheme()
  const { goTo } = useModals()
  const { id } = useParams()
  const { patient } = usePatientContext()
  const { eligibilities, isLoading, isError } = useEligibilities(id)
  const { onOpenSnackbar } = useSnackbarContext()

  useEffect(() => {
    if (!isLoading && !!isError) {
      onOpenSnackbar(
        "It wasn't possible to load the patient eligibilities.",
        'error'
      )
    }
  }, [isLoading, isError, onOpenSnackbar])

  if (isLoading) {
    return <LoadingState>Loading...</LoadingState>
  }

  const handleEligibilityCheck = () => {
    goTo(`/v2/patient/${patient.id}/eligibility/check`, {
      modalType: 'create',
      patient,
      onClaimClick,
    })
  }

  if (!eligibilities || eligibilities.length === 0) {
    return (
      <>
        <ActionBar>
          <Button variant="text" onClick={handleEligibilityCheck}>
            Eligibility Check
          </Button>
        </ActionBar>
        <EmptyState>No eligibilities found</EmptyState>
      </>
    )
  }

  const handleView = eligibility => {
    goTo(`/v2/patient/${patient.id}/eligibility/show`, {
      modalType: 'show',
      eligibility,
      patient,
      onClaimClick,
    })
  }

  const getStatusColor = status => {
    const colors = {
      failed: { bg: '#ffebee', color: theme.palette.error.main },
      created: { bg: '#e8f5e9', color: theme.palette.success.main },
      requested: { bg: '#fff3e0', color: theme.palette.warning.main },
      requested_with_error: { bg: '#ffebee', color: theme.palette.error.main },
    }
    return colors[status] || { bg: '#f5f5f5', color: '#666' }
  }

  const formatStatus = status => {
    return status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'
  }

  return (
    <>
      <RTEligibilities />
      <ActionBar>
        <Button variant="text" onClick={handleEligibilityCheck}>
          Eligibility Check
        </Button>
      </ActionBar>
      {eligibilities.map(eligibility => {
        const statusColors = getStatusColor(eligibility.status)
        return (
          <EligibilityCard key={eligibility.id}>
            <CardHeader>
              <div>
                <ProviderName>
                  {eligibility.provider?.firstName ||
                    eligibility.provider?.name ||
                    'N/A'}{' '}
                  {eligibility.provider?.lastName || ''}
                </ProviderName>
              </div>
              <StatusBadge bgColor={statusColors.bg} color={statusColors.color}>
                {formatStatus(eligibility.status)}
              </StatusBadge>
            </CardHeader>

            <InfoRow>
              <InfoLabel>Insurance</InfoLabel>
              <InfoValue>
                {eligibility.insurance?.payer?.name || 'N/A'}
              </InfoValue>
            </InfoRow>

            <InfoRow>
              <InfoLabel>Created</InfoLabel>
              <InfoValue>
                {safeFormat(parseISO(eligibility.createdAt), 'MMM dd, yyyy')}
              </InfoValue>
            </InfoRow>

            {eligibility.provider?.npi && (
              <InfoRow>
                <InfoLabel>NPI</InfoLabel>
                <InfoValue>{eligibility.provider.npi}</InfoValue>
              </InfoRow>
            )}

            <CardActions>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleView(eligibility)}
                fullWidth
              >
                View Details
              </Button>
            </CardActions>
          </EligibilityCard>
        )
      })}
    </>
  )
}

export default EligibilitiesTabMobile
